import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Check, ChevronDown, ChevronUp, Lock, Undo2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import { EmptyState } from '../../../shared/components/ui/primitives';
import {
  approvePayrollPeriod,
  decideApprovalPayment,
  decideRateChange,
  getApprovalPayrollPayslips,
  getApprovalsInbox,
  returnPayrollPeriod,
  reopenApprovalPayroll
} from '../../services/api/implementation/impl';
import { useApproverAccess } from '../../hooks/useApproverAccess';

const TABS = [
  { key: 'payments', type: 'LABOUR_PAYMENT', label: 'Wage payments' },
  { key: 'payroll', type: 'PAYROLL', label: 'Payroll' },
  { key: 'rates', type: 'WAGE_RATE_CHANGE', label: 'Rate changes' }
];

/* Same chip as ManagementHeaderTabs. */
const tabClass = (active) =>
  `rounded-md px-4 py-1.5 text-sm font-medium border transition-all duration-200 shrink-0 ${
    active
      ? 'bg-[#2A565E] text-white border-[#2A565E]'
      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
  }`;

/**
 * Everything waiting for the signed-in approver's final say.
 *
 * Lives inside Workforce, under its "Approvals" tab (29 Sep: taken out of the
 * sidebar). Only the account owner and the people he has assigned per type
 * see it; the chips are the types this person holds, and the server refuses a
 * decision from anybody else.
 */
export const ApprovalsPanel = () => {
  const access = useApproverAccess();
  const [searchParams, setSearchParams] = useSearchParams();

  const inbox = useQuery({
    queryKey: ['approvalsInbox'],
    queryFn: getApprovalsInbox,
    enabled: access.isApprover
  });

  const data = inbox.data?.data ?? {};
  const counts = inbox.data?.meta?.counts ?? {};
  const tabs = TABS.filter((t) => access.canApprove(t.type));
  const active = tabs.find((t) => t.key === searchParams.get('type')) ?? tabs[0];
  const selectType = (key) => {
    const next = new URLSearchParams(searchParams);
    if (key === tabs[0]?.key) next.delete('type');
    else next.set('type', key);
    setSearchParams(next);
  };

  if (!access.loaded && !access.failed) return <Loader />;
  if (!access.isApprover) {
    return (
      <EmptyState
        title="You have no approvals to give"
        hint="The account owner decides who approves wage payments, payroll and rate changes."
      />
    );
  }

  return (
    <div className="space-y-4">
      {tabs.length > 1 && (
        <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="Approval types">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={tab.key === active?.key}
              onClick={() => selectType(tab.key)}
              className={tabClass(tab.key === active?.key)}>
              {tab.label}
              {counts[tab.type] != null ? ` (${counts[tab.type]})` : ''}
            </button>
          ))}
        </div>
      )}
      {inbox.isLoading ? (
        <Loader />
      ) : active?.key === 'payments' ? (
        <PaymentsTab rows={data.payments ?? []} />
      ) : active?.key === 'payroll' ? (
        <PayrollTab rows={data.payroll ?? []} locked={data.payroll_locked ?? []} />
      ) : active?.key === 'rates' ? (
        <RatesTab rows={data.rate_changes ?? []} />
      ) : null}
    </div>
  );
};

/**
 * The old /builder/approvals address, kept so notifications and bookmarks
 * still land somewhere: it now opens Workforce on its Approvals tab, keeping
 * the type that was asked for (`?tab=payroll` becomes `&type=payroll`).
 */
const ApprovalsInbox = () => {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type') || searchParams.get('tab');
  const target = `/builder/workforce?tab=approvals${
    TABS.some((t) => t.key === type) ? `&type=${type}` : ''
  }`;
  return <Navigate to={target} replace />;
};

const useRefreshInbox = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['approvalsInbox'] });
};

/* ── Wage payments ─────────────────────────────────────────────────────── */

const PaymentsTab = ({ rows }) => {
  const queryClient = useQueryClient();
  const refresh = useRefreshInbox();
  const [rejecting, setRejecting] = useState(null);

  const decide = useMutation({
    mutationFn: ({ id, ...payload }) => decideApprovalPayment(id, payload),
    onSuccess: (_r, vars) => {
      refresh();
      queryClient.invalidateQueries({ queryKey: ['pendingLabourPayments'] });
      queryClient.invalidateQueries({ queryKey: ['labourDues'] });
      queryClient.invalidateQueries({ queryKey: ['labourLedger'] });
      setRejecting(null);
      toast.success(vars.approve === false ? 'Payment rejected' : 'Payment approved');
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not record the decision')
  });

  if (!rows.length) return <Empty />;

  return (
    <>
      <TableShell head={['Workman', 'Amount', 'Dated', 'Mode', 'Recorded by', '']}>
        {rows.map((row) => {
          const blocked = row.can_decide === false;
          return (
            <tr key={row.id} className={`border-t border-gray-100 ${blocked ? 'opacity-60' : ''}`}>
              <Td>
                <div className="font-medium text-gray-900">{row.worker?.name ?? '—'}</div>
                {row.project && <div className="text-xs text-gray-500">{row.project}</div>}
              </Td>
              <Td className="font-semibold tabular-nums">{money(row.amount)}</Td>
              <Td className="whitespace-nowrap">{shortDate(row.paid_on)}</Td>
              <Td className="text-xs">
                {titleCase(row.mode)}
                {row.reference && <div className="text-gray-500">{row.reference}</div>}
              </Td>
              <Td className="text-xs text-gray-600">{row.recorded_by ?? '—'}</Td>
              <Td>
                {blocked && (
                  <div className="mb-1 text-xs text-gray-500">
                    Recorded by you — needs another approver
                  </div>
                )}
                <DecisionButtons
                  disabled={decide.isPending || blocked}
                  onApprove={() => decide.mutate({ id: row.id, approve: true })}
                  onReject={() => setRejecting(row)}
                />
              </Td>
            </tr>
          );
        })}
      </TableShell>
      {rejecting && (
        <NoteModal
          title={`Reject ${money(rejecting.amount)} to ${rejecting.worker?.name ?? 'workman'}`}
          label="Reason"
          placeholder="Why this is not being paid"
          confirm="Reject payment"
          danger
          busy={decide.isPending}
          onClose={() => setRejecting(null)}
          onSubmit={(reason) => decide.mutate({ id: rejecting.id, approve: false, reason })}
        />
      )}
    </>
  );
};

/* ── Payroll ───────────────────────────────────────────────────────────── */

const PayrollTab = ({ rows, locked = [] }) => {
  const queryClient = useQueryClient();
  const refresh = useRefreshInbox();
  const [open, setOpen] = useState(null);
  const [returning, setReturning] = useState(null);
  const [reopening, setReopening] = useState(null);

  const onDone = (message) => {
    refresh();
    queryClient.invalidateQueries({ queryKey: ['payrollPeriods'] });
    setReturning(null);
    setReopening(null);
    toast.success(message);
  };
  const onFail = (err) =>
    toast.error(err?.response?.data?.message || 'Could not update the period');

  const approve = useMutation({
    mutationFn: (id) => approvePayrollPeriod(id),
    onSuccess: () => onDone('Payroll approved and locked'),
    onError: onFail
  });
  const sendBack = useMutation({
    mutationFn: ({ id, note }) => returnPayrollPeriod(id, note),
    onSuccess: () => onDone('Sent back to HR'),
    onError: onFail
  });
  const reopen = useMutation({
    mutationFn: ({ id, reason }) => reopenApprovalPayroll(id, reason),
    onSuccess: () => onDone('Payroll reopened — HR can recompute it'),
    onError: onFail
  });
  const busy = approve.isPending || sendBack.isPending || reopen.isPending;

  if (!rows.length && !locked.length) return <Empty />;

  return (
    <div className="space-y-3">
      {!rows.length && (
        <p className="text-sm text-gray-500">Nothing waiting for your approval.</p>
      )}
      {rows.map((p) => (
        <div key={p.id} className="rounded-panel border border-hairline bg-surface">
          <div className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div>
              <div className="text-base font-semibold text-gray-900">
                {p.label || `${p.month}/${p.year}`}
              </div>
              <div className="mt-0.5 text-xs text-gray-500">
                {p.employee_count} employee(s) · gross {money(p.totals?.gross)} · net{' '}
                <span className="font-medium text-gray-800">{money(p.totals?.net)}</span>
                {p.approval_requested_at && <> · sent {shortDate(p.approval_requested_at)}</>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setOpen(open === p.id ? null : p.id)}
                className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700">
                {open === p.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />} View payslips
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => approve.mutate(p.id)}
                className="flex items-center gap-1 rounded-lg bg-brand-700 px-3 py-1.5 text-sm text-white disabled:opacity-50">
                <Lock size={14} /> Approve &amp; lock
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => setReturning(p)}
                className="flex items-center gap-1 rounded-lg border border-warning-border px-3 py-1.5 text-sm text-warning-fg disabled:opacity-50">
                <Undo2 size={14} /> Send back
              </button>
            </div>
          </div>
          {open === p.id && <PeriodPayslips periodId={p.id} />}
        </div>
      ))}
      {/* Months already approved. A payroll approver may have no Payroll
          screen of his own, so this is where he sees them again and, if a
          figure is wrong, reopens one (UAT, 30 Sep). */}
      {locked.length > 0 && (
        <div className="space-y-3 pt-2">
          <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Approved &amp; locked
          </h3>
          {locked.map((p) => (
            <div key={p.id} className="rounded-panel border border-hairline bg-surface">
              <div className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div>
                  <div className="text-base font-semibold text-gray-900">
                    {p.label || `${p.month}/${p.year}`}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-500">
                    {p.employee_count} employee(s) · net{' '}
                    <span className="font-medium text-gray-800">{money(p.totals?.net)}</span>
                    {p.locked_at && <> · locked {shortDate(p.locked_at)}</>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setOpen(open === p.id ? null : p.id)}
                    className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700">
                    {open === p.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />} View
                    payslips
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setReopening(p)}
                    className="flex items-center gap-1 rounded-lg border border-warning-border px-3 py-1.5 text-sm text-warning-fg disabled:opacity-50">
                    <Undo2 size={14} /> Reopen
                  </button>
                </div>
              </div>
              {open === p.id && <PeriodPayslips periodId={p.id} />}
            </div>
          ))}
        </div>
      )}
      {reopening && (
        <NoteModal
          title={`Reopen ${reopening.label || 'this period'}`}
          label="Why it needs reopening"
          placeholder="e.g. Priya's September LOP was wrong"
          confirm="Reopen"
          busy={reopen.isPending}
          onClose={() => setReopening(null)}
          onSubmit={(reason) => reopen.mutate({ id: reopening.id, reason })}
        />
      )}
      {returning && (
        <NoteModal
          title={`Send ${returning.label || 'this period'} back to HR`}
          label="What needs to change"
          placeholder="e.g. Ravi's LOP days look wrong"
          confirm="Send back"
          busy={sendBack.isPending}
          onClose={() => setReturning(null)}
          onSubmit={(note) => sendBack.mutate({ id: returning.id, note })}
        />
      )}
    </div>
  );
};

const PeriodPayslips = ({ periodId }) => {
  const slips = useQuery({
    queryKey: ['approvalPayslips', periodId],
    queryFn: () => getApprovalPayrollPayslips(periodId)
  });
  const rows = slips.data?.data ?? [];

  if (slips.isLoading) return <Loader />;
  return (
    <div className="overflow-x-auto border-t border-hairline">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            <Th>Employee</Th>
            <Th>Gross</Th>
            <Th>Deductions</Th>
            <Th>Net</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={4} className="py-6 text-center text-gray-500">
                No payslips in this period.
              </td>
            </tr>
          )}
          {rows.map((slip) => (
            <tr key={slip.id} className="border-t border-gray-100">
              <Td className="font-medium text-gray-900">{slip.user?.name ?? `#${slip.user_id}`}</Td>
              <Td>{money(slip.gross)}</Td>
              <Td>{money(slip.total_deductions)}</Td>
              <Td className="font-medium">{money(slip.net)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ── Rate changes ──────────────────────────────────────────────────────── */

const RatesTab = ({ rows }) => {
  const queryClient = useQueryClient();
  const refresh = useRefreshInbox();
  const [rejecting, setRejecting] = useState(null);

  const decide = useMutation({
    mutationFn: ({ id, ...payload }) => decideRateChange(id, payload),
    onSuccess: (result, vars) => {
      refresh();
      queryClient.invalidateQueries({ queryKey: ['labour'] });
      queryClient.invalidateQueries({ queryKey: ['labourRateRequests'] });
      setRejecting(null);
      if (vars.approve === false) {
        toast.success('Rate change rejected');
        return;
      }
      const n = Number(result?.repriced ?? 0);
      const locked = Number(result?.skipped_locked ?? 0);
      toast.success(
        `Approved — ${n} day${n === 1 ? '' : 's'} re-priced` +
          (locked ? ` (${locked} in a locked period left as they were)` : ''),
        { duration: locked ? 8000 : 4000 }
      );
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not record the decision')
  });

  if (!rows.length) return <Empty />;

  return (
    <>
      <TableShell head={['Workman', 'Day rate', 'Starts from', 'Reason', 'Requested by', '']}>
        {rows.map((row) => {
          const blocked = row.can_decide === false;
          const diff = Number(row.requested_rate) - Number(row.current_rate);
          return (
            <tr key={row.id} className={`border-t border-gray-100 ${blocked ? 'opacity-60' : ''}`}>
              <Td className="font-medium text-gray-900">{row.worker_name ?? '—'}</Td>
              <Td className="whitespace-nowrap tabular-nums">
                {money(row.current_rate)} →{' '}
                <span className="font-semibold">{money(row.requested_rate)}</span>
                {Number.isFinite(diff) && diff !== 0 && (
                  <span
                    className={`ml-1 text-xs ${diff > 0 ? 'text-warning-fg' : 'text-success-fg'}`}>
                    ({diff > 0 ? '+' : '−'}
                    {money(Math.abs(diff))})
                  </span>
                )}
              </Td>
              <Td className="whitespace-nowrap">{shortDate(row.effective_from)}</Td>
              <Td className="max-w-xs text-xs text-gray-700">{row.reason || '—'}</Td>
              <Td className="text-xs text-gray-600">
                {row.requested_by_name ?? '—'}
                <div className="text-gray-400">{shortDate(row.created_at)}</div>
              </Td>
              <Td>
                {blocked && (
                  <div className="mb-1 text-xs text-gray-500">
                    Requested by you — needs another approver
                  </div>
                )}
                <DecisionButtons
                  disabled={decide.isPending || blocked}
                  onApprove={() => decide.mutate({ id: row.id, approve: true })}
                  onReject={() => setRejecting(row)}
                />
              </Td>
            </tr>
          );
        })}
      </TableShell>
      {rejecting && (
        <NoteModal
          title={`Reject rate change for ${rejecting.worker_name ?? 'workman'}`}
          label="Reason"
          placeholder="Why the rate stays as it is"
          confirm="Reject"
          danger
          busy={decide.isPending}
          onClose={() => setRejecting(null)}
          onSubmit={(note) => decide.mutate({ id: rejecting.id, approve: false, note })}
        />
      )}
    </>
  );
};

/* ── Shared bits ───────────────────────────────────────────────────────── */

const Empty = () => <EmptyState title="Nothing waiting for you" />;

const DecisionButtons = ({ disabled, onApprove, onReject }) => (
  <div className="flex gap-2">
    <button
      type="button"
      disabled={disabled}
      onClick={onApprove}
      className="flex items-center gap-1 rounded bg-brand-700 px-2.5 py-1 text-xs text-white disabled:cursor-not-allowed disabled:opacity-40">
      <Check size={12} /> Approve
    </button>
    <button
      type="button"
      disabled={disabled}
      onClick={onReject}
      className="flex items-center gap-1 rounded border border-danger-border px-2.5 py-1 text-xs text-danger-fg disabled:cursor-not-allowed disabled:opacity-40">
      <X size={12} /> Reject
    </button>
  </div>
);

const NoteModal = ({ title, label, placeholder, confirm, danger, busy, onClose, onSubmit }) => {
  const [text, setText] = useState('');
  return (
    <Modal name={title} onClose={onClose} width="460px">
      <div className="space-y-3">
        <div>
          <div className="mb-1 text-xs font-medium text-gray-600">{label}</div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            placeholder={placeholder}
          />
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700">
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || !text.trim()}
            onClick={() => onSubmit(text.trim())}
            className={`rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${
              danger ? 'bg-danger' : 'bg-warning'
            }`}>
            {busy ? 'Saving…' : confirm}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const TableShell = ({ head, children }) => (
  <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            {head.map((h, i) => (
              <Th key={i}>{h}</Th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  </div>
);

const Th = ({ children }) => (
  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">{children}</th>
);
const Td = ({ children, className = '' }) => (
  <td className={`px-4 py-3 align-top ${className}`}>{children}</td>
);

function money(value) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return '₹0';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}
function shortDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
}
function titleCase(value) {
  if (!value) return '—';
  return value
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export default ApprovalsInbox;
